import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/config";

const SubscribePage = ({ userProfile }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [error, setError] = useState("");
  const [paidSubscriptionsOpen, setPaidSubscriptionsOpen] = useState(false);
  const [launchLoading, setLaunchLoading] = useState(true);

  const params = new URLSearchParams(location.search);
  const success = params.get("success") === "true";
  const canceled = params.get("canceled") === "true";

  const fromPath = location.state?.from?.pathname || "/home";

  const isFounder =
    userProfile?.founder === true ||
    userProfile?.isFounder === true ||
    userProfile?.founderAccount === true ||
    userProfile?.subscriptionStatus === "free_lifetime" ||
    userProfile?.plan === "founder";

  const hasPremium =
    userProfile?.premiumAccess === true ||
    userProfile?.subscriptionStatus === "active" ||
    userProfile?.subscriptionStatus === "trialing";

  const hasStripeCustomer = !!userProfile?.stripeCustomerId;

  useEffect(() => {
    const loadLaunchConfig = async () => {
      try {
        const snap = await getDoc(doc(db, "appConfig", "launch"));
        const data = snap.exists() ? snap.data() : {};

        setPaidSubscriptionsOpen(data.paidSubscriptionsOpen === true);
      } catch (error) {
        console.error("Failed loading launch config:", error);
        setPaidSubscriptionsOpen(false);
      } finally {
        setLaunchLoading(false);
      }
    };

    loadLaunchConfig();
  }, []);

  const startCheckout = async () => {
    try {
      setError("");
      setLoadingCheckout(true);

      const functions = getFunctions(undefined, "us-central1");
      const createCheckoutSession = httpsCallable(
        functions,
        "createCheckoutSession"
      );

      const result = await createCheckoutSession();
      const url = result?.data?.url;

      if (!url) {
        throw new Error("Stripe checkout did not return a checkout URL.");
      }

      window.location.href = url;
    } catch (err) {
      console.error("Stripe checkout failed:", err);
      setError(
        err?.message ||
          "Checkout could not be started. Please try again in a moment."
      );
    } finally {
      setLoadingCheckout(false);
    }
  };

  const openBillingPortal = async () => {
    try {
      setError("");
      setLoadingPortal(true);

      const functions = getFunctions(undefined, "us-central1");
      const createBillingPortalSession = httpsCallable(
        functions,
        "createBillingPortalSession"
      );

      const result = await createBillingPortalSession();
      const url = result?.data?.url;

      if (!url) {
        throw new Error("Stripe billing portal did not return a URL.");
      }

      window.location.href = url;
    } catch (err) {
      console.error("Billing portal failed:", err);
      setError(
        err?.message ||
          "Billing portal could not be opened. Please try again in a moment."
      );
    } finally {
      setLoadingPortal(false);
    }
  };

  if (isFounder) {
    return (
      <div className="card page-shell page-shell-narrow">
        <h2>Founder lifetime access active 🌟</h2>
        <p>Your founder account has free lifetime access.</p>

        <button className="btn-primary" onClick={() => navigate(fromPath)}>
          Continue
        </button>
      </div>
    );
  }

  if (hasPremium) {
    return (
      <div className="card page-shell page-shell-narrow">
        <h2>MyFarmingPal Premium active ✅</h2>
        <p>
          Your Premium access is active. You can post jobs, bid or apply for
          work, sell products, and host events.
        </p>

        {success && (
          <div className="info-bar" style={{ marginBottom: "12px" }}>
            Payment successful. Your account may take a few seconds to update.
          </div>
        )}

        {error && (
          <div className="error-banner" style={{ marginBottom: "12px" }}>
            {error}
          </div>
        )}

        <button className="btn-primary" onClick={() => navigate(fromPath)}>
          Continue
        </button>

        {hasStripeCustomer && (
          <button
            className="btn-soft"
            type="button"
            onClick={openBillingPortal}
            disabled={loadingPortal}
          >
            {loadingPortal ? "Opening billing..." : "Manage billing"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="card page-shell page-shell-narrow">
      <h2>MyFarmingPal Premium</h2>

      {success && (
        <div className="info-bar" style={{ marginBottom: "12px" }}>
          Payment successful. Your account is updating now. Refresh in a few
          seconds if Premium does not show yet.
        </div>
      )}

      {canceled && (
        <div className="info-bar" style={{ marginBottom: "12px" }}>
          Checkout was canceled. You can keep browsing for free.
        </div>
      )}

      {error && (
        <div className="error-banner" style={{ marginBottom: "12px" }}>
          {error}
        </div>
      )}

      <p>
        Browse and buy local products for free. Premium unlocks posting jobs,
        applying or bidding for work, selling products, and hosting events.
      </p>

      <div className="premium-feature-list">
        <p>✅ Post field jobs and day jobs</p>
        <p>✅ Bid/apply for local farm work</p>
        <p>✅ Sell products and add farm stands</p>
        <p>✅ Host events and manage vendor reservations</p>
      </div>

            {launchLoading ? (
        <button className="btn-primary" type="button" disabled>
          Checking access...
        </button>
      ) : paidSubscriptionsOpen ? (
        <button
          className="btn-primary"
          type="button"
          onClick={startCheckout}
          disabled={loadingCheckout}
        >
          {loadingCheckout ? "Opening checkout..." : "Subscribe"}
        </button>
      ) : (
        <div className="info-bar" style={{ marginTop: "12px" }}>
          Founder early access is currently open. Paid subscriptions will open
          after the first founder group is complete.
        </div>
      )}

      {hasStripeCustomer && (
        <button
          className="btn-soft"
          type="button"
          onClick={openBillingPortal}
          disabled={loadingPortal}
        >
          {loadingPortal ? "Opening billing..." : "Manage billing"}
        </button>
      )}

      <button
        className="btn-soft"
        type="button"
        onClick={() => navigate("/home")}
      >
        Keep browsing for free
      </button>
    </div>
  );
};

export default SubscribePage;